import { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/* components */
import BundleItem from "@components/plp/BundleItem";
import BundleGroupItem from "@components/plp/BundleGroupItem";
import Skeleton from "@components/plp/Skeleton";
import { productImpressions } from "@utils/analytics";

/* styles */
import style from "@styles/plp/BundleList.module.scss";

/* context */
import context from "@context/plp";

export default function CollectionList({ preview, bundles, prices }) {
  const { state } = useContext(context);
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(6);
  const [loadingMore, setLoadingMore] = useState(false);
  // Use ref to avoid re-running effect due to local state changes
  const impressionsRef = useRef([]);
  const lastPricesRef = useRef(prices);

  const applyFilters = (bundle) => {
    const { active: activeFilters } = state.filters;

    // Check gender filter
    if (activeFilters.gender) {
      const genderToCheck = [...activeFilters.gender];
      if (genderToCheck.includes("men") || genderToCheck.includes("women")) {
        genderToCheck.push("unisex");
      }

      const gender = bundle.gender;
      if (!gender || !genderToCheck.includes(gender)) {
        return false;
      }
    }

    return true;
  };

  const createRenderList = () => {
    const { active: activeFilters } = state.filters;
    const locale = document
      .querySelector("[data-view-id]")
      .getAttribute("data-view-id");

    const subBundlesFiltered = bundles.map((collectionGroup) => {
      var filteredBundles = collectionGroup.bundles.filter((bundles) =>
        bundles.view_id.includes(locale),
      );
      var filteredCollectionGroup = collectionGroup;
      filteredCollectionGroup.bundles = filteredBundles;
      return filteredCollectionGroup;
    });

    const collectionGroupsReduced = subBundlesFiltered.filter(
      (collectionGroup) => {
        return (
          collectionGroup.view_id.includes(locale) &&
          collectionGroup.bundles.length > 0
        );
      },
    );

    return bundles
      .map((bundleOrGroup) => {
        if (activeFilters.collection) {
          const associatedLowerCase = bundleOrGroup.associated
            ? bundleOrGroup.associated.toLowerCase()
            : "";
          const activeCollectionsLowerCase = activeFilters.collection
            .filter((c) => typeof c === "string")
            .map((c) => c.toLowerCase());

          if (
            !associatedLowerCase ||
            !activeCollectionsLowerCase.includes(associatedLowerCase)
          ) {
            return null;
          }
        }

        if (bundleOrGroup.gender) {
          return applyFilters(bundleOrGroup) ? bundleOrGroup : null;
        } else {
          const filteredBundles = bundleOrGroup.bundles.filter((bundle) =>
            applyFilters(bundle),
          );
          return filteredBundles.length > 0
            ? { ...bundleOrGroup, bundles: filteredBundles }
            : null;
        }
      })
      .filter(Boolean);
  };

  let renderList = createRenderList();

  useEffect(() => {}, [renderList]);

  const hasThreeItemsBundles = renderList.some(
    (b) => b.products && b.products.length > 2,
  );
  const visibleItems = renderList.slice(0, visibleCount);

  const emitImpressions = (items, { reset } = { reset: false }) => {
    if (!Array.isArray(prices) || prices.length === 0) return;
    const toSend = [];
    const tmpImpressions = reset
      ? []
      : JSON.parse(JSON.stringify(impressionsRef.current));
    let initialIndex = -1;
    const combinedBundles = [];
    for (const index in items) {
      if (tmpImpressions.indexOf(items[index].id) < 0) {
        toSend.push(items[index]);
        tmpImpressions.push(items[index].id);
      } else {
        const item = items[index];
        if (item.bundles) {
          combinedBundles.push(...item.bundles);
        } else {
          combinedBundles.push(item);
        }
      }
    }
    if (toSend.length) {
      for (const index in combinedBundles) {
        const item = combinedBundles[index];
        try {
          const products = JSON.parse(item.products || "[]");
          initialIndex += products.length;
        } catch (e) {}
      }
      productImpressions(toSend, initialIndex, prices);
      impressionsRef.current = tmpImpressions;
    }
  };

  // Fire when prices become available and when showing more items
  useEffect(() => {
    const pricesChanged = lastPricesRef.current !== prices;
    lastPricesRef.current = prices;
    if (!Array.isArray(prices) || prices.length === 0) return;
    emitImpressions(visibleItems, { reset: pricesChanged });
  }, [prices, visibleCount]);

  const handleShowMore = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 50);

    setLoadingMore(true);

    setTimeout(() => {
      setVisibleCount(visibleCount + 6);
      setLoadingMore(false);

      setTimeout(() => {
        window.scrollBy({
          top: 1,
          behavior: "smooth",
        });

        setTimeout(() => {
          window.scrollBy({
            top: -1,
            behavior: "smooth",
          });
        }, 50);
      }, 100);
    }, 1500);
  };

  return (
    <div
      className={`${style.container} ${state.filters.active ? style.activeFilters : ""} ${state.filtering ? style.hidden : ""}`}
    >
      {state.loading ? (
        <Skeleton visible={true} />
      ) : (
        <>
          {visibleItems.map((item, index) => {
            if (item.gender) {
              return (
                <BundleItem
                  key={index}
                  prices={prices}
                  bundle={item}
                  preview={preview}
                  threeItemsLayout={hasThreeItemsBundles}
                />
              );
            } else {
              return (
                <BundleGroupItem
                  key={index}
                  bundleGroup={item}
                  preview={preview}
                  prices={prices}
                  context="collection"
                />
              );
            }
          })}
          {visibleCount < renderList.length && (
            <div className={style.showMoreContainer}>
              {loadingMore ? (
                <Skeleton visible={loadingMore} />
              ) : (
                <button onClick={handleShowMore} className={style.showMore}>
                  {t("plp.SHOW-MORE")}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
